/**
 * DocumentRenderer - Renders documents using Owl Ops API.
 *
 * Replaces the old generateDocument function with API-based rendering.
 * This component fetches HTML from the backend and displays it in an iframe.
 */

import { useEffect, useState, useRef, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { documentsApi, publicDocumentApi } from '@/lib/api';
import { generateBannerHtml, getIconSvgFromUrl } from './utils';
import type {
  DocumentData,
  BusinessData,
  TemplateSettings,
} from './types';

interface DocumentRendererProps {
  data: DocumentData;
  settings: TemplateSettings;
  business: BusinessData;
  className?: string;
  /** Use public API endpoint (for unauthenticated pages) */
  isPublic?: boolean;
}

/**
 * Build Owl Ops API payload from document data and settings
 */
export function buildOwlOpsPayload(
  documentData: DocumentData,
  business: BusinessData,
  settings: TemplateSettings
): { data: Record<string, unknown>; settings: Record<string, unknown> } {
  const docType = documentData.documentType;

  const getNumber = (): string => {
    switch (docType) {
      case 'invoice': return (documentData as { invoice_number?: string }).invoice_number || '';
      case 'quote': return (documentData as { quote_number?: string }).quote_number || '';
      case 'credit_memo': return (documentData as { credit_memo_number?: string }).credit_memo_number || '';
      case 'purchase_order': return (documentData as { purchase_order_number?: string }).purchase_order_number || '';
      default: return '';
    }
  };

  const getDate = (): string => {
    switch (docType) {
      case 'invoice': return (documentData as { invoice_date?: string }).invoice_date || '';
      case 'quote': return (documentData as { quote_date?: string }).quote_date || '';
      case 'credit_memo': return (documentData as { credit_memo_date?: string }).credit_memo_date || '';
      case 'purchase_order': return (documentData as { issued_date?: string }).issued_date || '';
      default: return '';
    }
  };

  const getNotes = (): string => {
    switch (docType) {
      case 'invoice':
      case 'quote':
      case 'credit_memo':
        return (documentData as { note_for_client?: string }).note_for_client || '';
      case 'purchase_order':
        return (documentData as { note_for_vendor?: string }).note_for_vendor || '';
      default: return '';
    }
  };

  // Build columns from settings
  const columnTypeMap: Record<string, 'text' | 'number' | 'currency' | 'date' | 'percent'> = {
    name: 'text',
    code: 'text',
    description: 'text',
    quantity: 'number',
    rate: 'currency',
    tax: 'percent',
    amount: 'currency',
  };
  const columns = (settings.tableColumns || [])
    .filter(col => col.enabled)
    .map(col => ({
      key: col.id,
      label: col.name,
      type: columnTypeMap[col.id] || 'text',
      ...(col.width ? { width: col.width } : {}),
    }));

  // Build header fields (only custom fields for current doc type)
  // If document has custom_field_values, respect per-document toggles (only show fields with keys present)
  const docCustomValues = (documentData as { custom_field_values?: Record<string, string> }).custom_field_values;
  const header_fields = (settings.headerFields || [])
    .filter(field => {
      if (!field.enabled || !field.isCustom || !field.documentTypes.includes(docType)) return false;
      // If document has per-doc values, only include fields that are toggled on
      if (docCustomValues !== undefined && !(field.id in docCustomValues)) return false;
      return true;
    })
    .map(field => ({
      key: field.id,
      label: field.name,
      enabled: true,
      is_custom: true,
      value: docCustomValues?.[field.id] ?? field.defaultValue ?? '',
    }));

  // Helper to check if a built-in field is enabled
  const isFieldEnabled = (fieldId: string): boolean => {
    const field = settings.headerFields.find(f => f.id === fieldId && !f.isCustom);
    return field ? field.enabled : true;
  };

  // Build line_item_sections (don't send duplicate line_items)
  const line_item_sections: Array<{ name: string; items: Array<Record<string, unknown>> }> = [];

  if (documentData.line_items) {
    for (const [sectionName, items] of documentData.line_items) {
      const sectionItems = items.map(item => {
        const transformed: Record<string, unknown> = {
          name: item.name,
          quantity: item.quantity,
          rate: item.rate,
          tax: item.tax_rate,
          amount: item.amount,
        };
        // Only include code/description if they have values
        if (item.code) transformed.code = item.code;
        if (item.description) transformed.description = item.description;
        return transformed;
      });
      line_item_sections.push({ name: sectionName, items: sectionItems });
    }
  }

  // Build totals
  const totals: Array<{
    key: string;
    label: string;
    value: number;
    style?: string;
    divider?: string;
  }> = [];
  if ('subtotal' in documentData) {
    totals.push({ key: 'subtotal', label: 'Subtotal', value: documentData.subtotal as number, style: 'default' });
  }
  // Discount comes before tax
  if ('total_discount' in documentData && (documentData as { total_discount?: number }).total_discount) {
    totals.push({ key: 'discount', label: 'Discount', value: -(documentData as { total_discount: number }).total_discount, style: 'addition' });
  }
  if ('total_tax' in documentData && (documentData as { total_tax?: number }).total_tax) {
    totals.push({ key: 'tax', label: 'Tax', value: (documentData as { total_tax: number }).total_tax, style: 'default' });
  }
  // Shipping comes after tax (shipping is not taxable)
  if ((documentData as { ship_enabled?: boolean }).ship_enabled) {
    const shipFee = (documentData as { ship_fee?: string | number }).ship_fee;
    if (shipFee) {
      const feeValue = typeof shipFee === 'string' ? parseFloat(shipFee) : shipFee;
      if (feeValue > 0) {
        totals.push({ key: 'shipping', label: 'Shipping', value: feeValue, style: 'default' });
      }
    }
  }
  // NOTE: Markup is intentionally NOT shown - it's internal pricing info, not for customer-facing documents
  // Only emphasize balance_due, not total (template uses first emphasized for the big box)
  const hasBalanceDue = 'balance_due' in documentData && (documentData as { balance_due?: number }).balance_due;
  if ('total_amount' in documentData) {
    totals.push({ key: 'total', label: 'Total', value: (documentData as { total_amount: number }).total_amount, style: 'default', divider: 'above' });
  }
  if ('amount_paid' in documentData && (documentData as { amount_paid?: number }).amount_paid) {
    totals.push({ key: 'paid', label: 'Amount Paid', value: -(documentData as { amount_paid: number }).amount_paid, style: 'addition' });
  }
  if (hasBalanceDue) {
    totals.push({ key: 'balance', label: 'Balance Due', value: (documentData as { balance_due: number }).balance_due, style: 'emphasized', divider: 'above' });
  }

  // Build client/vendor
  let client: Record<string, unknown> | undefined;
  let vendor: Record<string, unknown> | undefined;

  if (docType === 'purchase_order') {
    vendor = {
      id: 'vendor',
      name: (documentData as { vendor_name?: string }).vendor_name || '',
      company: (documentData as { vendor_company?: string }).vendor_company || '',
      address: (documentData as { vendor_address?: string }).vendor_address || '',
    };
  } else {
    client = {
      id: 'client',
      name: (documentData as { client_name?: string }).client_name || '',
      company: (documentData as { client_company?: string }).client_company || '',
      address: (documentData as { client_address?: string }).client_address || '',
    };
  }

  // Build business
  // Logo priority: settings.logo.url (template designer) > business.logo
  // Set logo to null if it's an icon: reference (template uses settings.logo.iconSvg instead)
  const logoUrl = settings.logo?.url || business.logo;
  const businessLogo = logoUrl && !logoUrl.startsWith('icon:') ? logoUrl : null;
  const businessObj: Record<string, unknown> = {
    name: business.name,
    address: business.address,
    logo: businessLogo,
  };

  // Build document object
  const documentObj: Record<string, unknown> = {
    type: docType,
    number: getNumber(),
    date: getDate(),
    line_item_sections,
    columns,
    header_fields,
    totals,
  };

  // Only include status if 'paid' - other statuses are not customer-facing
  const rawStatus = (documentData as { status?: string }).status;
  if (rawStatus === 'paid') {
    documentObj.status = 'paid';
  }

  // Only include notes if there's actual content
  const notes = getNotes();
  if (notes) {
    documentObj.notes = notes;
  }

  // Only include due_date if enabled
  if (isFieldEnabled('due_date')) {
    documentObj.due_date = (documentData as { due_date?: string }).due_date;
  }

  // Only include payment_terms if enabled
  if (isFieldEnabled('terms')) {
    const rawTerms = (documentData as { payment_terms?: string }).payment_terms;
    const formatTerms = (terms: string | undefined): string | undefined => {
      if (!terms) return terms;
      if (terms.includes(' ') || /^[A-Z]/.test(terms)) return terms;
      return terms.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };
    documentObj.payment_terms = formatTerms(rawTerms);
  }

  // Include signatures if provided (captured signatures)
  if (documentData.signatures && documentData.signatures.length > 0) {
    documentObj.signatures = documentData.signatures;
  }

  // Include signature_lines if provided (expected signatures not yet captured)
  if (documentData.signature_lines && documentData.signature_lines.length > 0) {
    documentObj.signature_lines = documentData.signature_lines;
  }

  // Include contract if provided and has content (avoid empty "Contract Terms" box)
  if (documentData.contract && documentData.contract.content) {
    documentObj.contract = documentData.contract;
  }

  // Build data object for API
  const data: Record<string, unknown> = {
    document: documentObj,
    business: businessObj,
  };

  // Include shipping info if enabled on document AND ship_to field is enabled in template
  // Ship To is the "master switch" - metadata fields only show when Ship To is enabled
  if ((documentData as { ship_enabled?: boolean }).ship_enabled && isFieldEnabled('ship_to')) {
    const shipDate = (documentData as { ship_date?: string }).ship_date;
    const shipVia = (documentData as { ship_via?: string }).ship_via;
    const shipTracking = (documentData as { ship_tracking_number?: string }).ship_tracking_number;

    // Add shipping metadata to header_fields (only if individual field is enabled and has a value)
    if (shipDate && isFieldEnabled('ship_date')) {
      header_fields.push({ key: 'ship_date', label: 'Ship Date', enabled: true, is_custom: false, value: shipDate });
    }
    if (shipVia && isFieldEnabled('ship_via')) {
      header_fields.push({ key: 'ship_via', label: 'Ship Via', enabled: true, is_custom: false, value: shipVia });
    }
    if (shipTracking && isFieldEnabled('tracking_no')) {
      header_fields.push({ key: 'tracking_no', label: 'Tracking No', enabled: true, is_custom: false, value: shipTracking });
    }

    // Build shipping address block (Ship To)
    if (docType === 'purchase_order') {
      // For PO: Ship To is the business address (where vendor ships to)
      data.shipping = {
        name: business.name,
        company: '',
        address: business.address || '',
      };
    } else {
      // For Invoice/Quote: Ship To uses client's primary address
      data.shipping = {
        name: (documentData as { client_name?: string }).client_name || '',
        company: (documentData as { client_company?: string }).client_company || '',
        address: (documentData as { client_address?: string }).client_address || '',
      };
    }
  }

  if (client) data.client = client;
  if (vendor) data.vendor = vendor;

  // Build settings object for API
  // Only enable banner if it has actual HTML content
  const bannerHtml = settings.banner.enabled
    ? generateBannerHtml(settings.banner.style, settings.colorTheme, settings.banner.height)
    : '';
  const bannerEnabled = settings.banner.enabled && Boolean(bannerHtml);

  // Pass business name for initials icon generation
  const logoIconSvg = getIconSvgFromUrl(settings.logo?.url, business.name);
  const watermarkIconSvg = settings.watermark.content?.startsWith('icon:')
    ? getIconSvgFromUrl(settings.watermark.content, business.name)
    : undefined;

  // Build footer
  const footer: Record<string, unknown> = {};
  if (settings.footer.genericNote?.enabled && settings.footer.genericNote?.content) {
    footer.genericNote = settings.footer.genericNote.content;
  }
  const docNote = settings.footer.documentNotes?.[docType];
  if (docNote?.enabled && docNote?.content) {
    footer.documentNotes = docNote.content;
  }
  if (settings.footer.termsAndConditions?.enabled && settings.footer.termsAndConditions?.content) {
    footer.termsAndConditions = settings.footer.termsAndConditions.content;
  }
  if (settings.footer.taxBreakdown !== undefined) {
    footer.taxBreakdown = settings.footer.taxBreakdown;
  }
  if (settings.footer.showPaymentInstructions && business.payment_instructions) {
    footer.paymentNotes = business.payment_instructions;
  }

  // Build logo settings
  const logo: Record<string, unknown> = {
    size: settings.logo?.size ?? 60,
    showCompanyName: settings.logo?.showCompanyName ?? true,
  };
  if (logoIconSvg) {
    logo.iconSvg = logoIconSvg;
  }

  // Build watermark settings
  let watermark: Record<string, unknown>;
  if (settings.watermark.enabled) {
    watermark = {
      enabled: true,
      type: settings.watermark.type,
      opacity: Number(settings.watermark.opacity),
      size: Number(settings.watermark.size),
      position: settings.watermark.position,
      rotation: Number(settings.watermark.rotation),
    };
    if (watermarkIconSvg) {
      watermark.iconSvg = watermarkIconSvg;
    } else if (settings.watermark.type === 'text' && settings.watermark.content) {
      watermark.content = settings.watermark.content;
    }
  } else {
    watermark = { enabled: false };
  }

  const apiSettings: Record<string, unknown> = {
    colorTheme: settings.colorTheme,
    banner: {
      enabled: bannerEnabled,
      height: `${settings.banner.height}px`,
      html: bannerHtml,
    },
    logo,
    watermark,
    footer,
    labels: settings.labels,
    dateFormat: settings.dateFormat,
    currency: business.currency_code || 'USD',
  };

  return { data, settings: apiSettings };
}

export function DocumentRenderer({ data, settings, business, className = '', isPublic = false }: DocumentRendererProps) {
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Memoize the payload to avoid unnecessary re-renders
  const payload = useMemo(
    () => buildOwlOpsPayload(data, business, settings),
    [data, business, settings]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Use public or authenticated API based on isPublic prop
    const api = isPublic ? publicDocumentApi : documentsApi;
    api.renderHtml({
      template_slug: settings.selectedLayout,
      data: payload.data,
      settings: payload.settings,
    }).then((response) => {
      if (cancelled) return;
      if (response.data?.html) {
        setHtml(response.data.html);
      } else if (response.error) {
        setError(response.error);
      }
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      setError(err.message || 'Failed to render document');
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [settings.selectedLayout, payload, isPublic]);

  // Update iframe content when HTML changes
  useEffect(() => {
    if (iframeRef.current && html) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(html);
        doc.close();
      }
    }
  }, [html]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-[500px] ${className}`}>
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !html) {
    return (
      <div className={`p-8 text-center text-gray-500 ${className}`}>
        {error || 'Unable to render document. Please try again.'}
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      className={`w-full bg-white ${className}`}
      style={{ minHeight: '1056px', border: 'none' }}
      title="Document Preview"
    />
  );
}
